import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import {
  Mail, Lock, Eye, EyeOff, LogIn, ArrowRight, Loader2, X, ExternalLink
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useScale, scaleW, scaleH } from "@/hooks/useScale";
import { COMPANY_SIGNUP_URL } from "@/lib/companyAuth";
import type { LoginDto } from "@shared/schemas/auth";

// Validation schema
const loginSchema = z.object({
  email: z.string().min(1, "Email or username is required"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginCardProps {
  onSuccess?: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
}

export default function LoginCard({ onSuccess, onClose, showCloseButton = false }: LoginCardProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const { login } = useAuth();
  const scale = useScale();
  // On mobile (< 768px), bypass the desktop scaling system and use fixed responsive values
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onLoginSubmit(data: LoginFormData) {
    setError(null);
    setLoading(true);

    try {
      await login(data.email, data.password);
      // Call success callback to reload data
      onSuccess?.();
    } catch (err: any) {
      setError(err.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
      style={{
        width: isMobile ? '100%' : `${scaleW(700, scale.w)}px`,
        maxWidth: isMobile ? '100%' : '90vw',
        position: 'relative',
        padding: isMobile ? '0 16px' : undefined,
      }}
    >
      <div
        className="backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden"
        style={{
          backgroundColor: theme.cardBg,
          border: `2px solid ${theme.accentColor}40`,
        }}
      >
        {/* Close button */}
        {showCloseButton && onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-white/10 transition-colors"
            style={{ color: theme.textColor }}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        )}

        {/* Header */}
        <div style={{
          padding: isMobile
            ? '32px 24px 24px'
            : `${scaleH(48, scale.h)}px ${scaleW(48, scale.w)}px ${scaleH(36, scale.h)}px`,
          textAlign: 'center'
        }}>
          <h1 className="font-bold mb-2" style={{
            fontSize: isMobile ? '26px' : `${scaleW(40, scale.w)}px`,
            lineHeight: '1.2'
          }}>
            <span style={{ color: theme.textColor }}>Welcome to </span>
            <span style={{ color: theme.textColor }}>Earth</span>
            <span style={{ color: '#E91E63' }}>Social</span>
          </h1>
          <p style={{
            fontSize: isMobile ? '15px' : `${scaleW(14, scale.w)}px`,
            color: theme.textColor,
            opacity: 0.8,
            marginTop: isMobile ? '8px' : `${scaleH(8, scale.h)}px`
          }}>
            Sign in with Company to continue your voyage
          </p>
        </div>

        {/* Form */}
        <div style={{
          padding: isMobile
            ? '0 24px 32px'
            : `0 ${scaleW(48, scale.w)}px ${scaleH(48, scale.h)}px`
        }}>
          <form onSubmit={loginForm.handleSubmit(onLoginSubmit)}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: isMobile ? '20px' : `${scaleH(24, scale.h)}px`
            }}>
              {/* Error message */}
              {error && (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/50 text-red-100 text-sm">
                  {error}
                </div>
              )}

              {/* Email field */}
              <div>
                <label className="block font-medium mb-2" style={{
                  color: theme.textColor,
                  opacity: 0.9,
                  fontSize: isMobile ? '15px' : undefined,
                }}>
                  Email or Username
                </label>
                <div className="relative">
                  <Mail className="absolute" style={{
                    width: isMobile ? '20px' : '1.25rem',
                    height: isMobile ? '20px' : '1.25rem',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.textColor,
                    opacity: 0.5,
                    pointerEvents: 'none'
                  }} />
                  <input
                    {...loginForm.register("email")}
                    type="text"
                    className="w-full rounded-lg focus:outline-none focus:ring-2 transition-all"
                    style={{
                      paddingLeft: '3rem',
                      paddingRight: '1rem',
                      paddingTop: isMobile ? '14px' : '0.875rem',
                      paddingBottom: isMobile ? '14px' : '0.875rem',
                      fontSize: isMobile ? '16px' : undefined,
                      minHeight: isMobile ? '48px' : undefined,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: theme.textColor,
                    }}
                    placeholder="Enter email or username"
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="mt-1 text-sm" style={{ color: '#fca5a5' }}>
                    {loginForm.formState.errors.email.message}
                  </p>
                )}
              </div>

              {/* Password field */}
              <div>
                <label className="block font-medium mb-2" style={{
                  color: theme.textColor,
                  opacity: 0.9,
                  fontSize: isMobile ? '15px' : undefined,
                }}>
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute" style={{
                    width: isMobile ? '20px' : '1.25rem',
                    height: isMobile ? '20px' : '1.25rem',
                    left: '1rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.textColor,
                    opacity: 0.5,
                    pointerEvents: 'none'
                  }} />
                  <input
                    {...loginForm.register("password")}
                    type={showPassword ? "text" : "password"}
                    className="w-full rounded-lg focus:outline-none focus:ring-2 transition-all"
                    style={{
                      paddingLeft: '3rem',
                      paddingRight: '3.5rem',
                      paddingTop: isMobile ? '14px' : '0.875rem',
                      paddingBottom: isMobile ? '14px' : '0.875rem',
                      fontSize: isMobile ? '16px' : undefined,
                      minHeight: isMobile ? '48px' : undefined,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: theme.textColor,
                    }}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute transition-colors"
                    style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)', color: theme.textColor, opacity: 0.5 }}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="mt-1 text-sm" style={{ color: '#fca5a5' }}>
                    {loginForm.formState.errors.password.message}
                  </p>
                )}
              </div>

              {/* Forgot password link */}
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="transition-colors"
                  style={{
                    color: theme.textColor,
                    opacity: 0.8,
                    fontSize: isMobile ? '15px' : '0.875rem',
                  }}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit button */}
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className="w-full font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  padding: isMobile ? '14px 1rem' : '0.875rem 1rem',
                  fontSize: isMobile ? '17px' : undefined,
                  minHeight: isMobile ? '52px' : undefined,
                  backgroundColor: theme.accentColor,
                  color: '#1a1a1a'
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Sign In
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </div>
          </form>

          {/* Sign up link — Company owns account creation post-merge, so we link
              directly to their signup with a `return` param. Same flow as the
              /register page; this skips one EarthSocial-side bounce. */}
          <div style={{
            marginTop: isMobile ? '24px' : `${scaleH(32, scale.h)}px`,
            textAlign: 'center'
          }}>
            <a
              href={`${COMPANY_SIGNUP_URL}?return=${encodeURIComponent(`${window.location.origin}/post-signup`)}`}
              className="transition-colors inline-flex items-center gap-1.5"
              style={{
                color: theme.textColor,
                opacity: 0.8,
                fontSize: isMobile ? '15px' : `${scaleW(14, scale.w)}px`
              }}
            >
              Don't have an account?{" "}
              <span className="font-semibold underline">Create one on Company</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
